import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Save, Clock, Lock, Unlock, AlertCircle } from "lucide-react";

const InputTimesPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [draws, setDraws] = useState([]);
  const [selectedDraw, setSelectedDraw] = useState("");
  const [currentDraw, setCurrentDraw] = useState(null);
  const [times, setTimes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    fetchPlaydays();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchDraws();
    }
  }, [selectedPlayday]);

  useEffect(() => {
    if (selectedDraw) {
      const draw = draws.find(d => d.id === selectedDraw);
      setCurrentDraw(draw);
      if (draw) {
        const initialTimes = {};
        draw.entries.forEach(entry => {
          initialTimes[entry.id] = {
            time: entry.time || "",
            penalty: entry.penalty || ""
          };
        });
        setTimes(initialTimes);
      }
    }
  }, [selectedDraw, draws]);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data);
      if (response.data.length > 0) {
        setSelectedPlayday(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  const fetchDraws = async () => {
    try {
      const response = await axios.get(`${API}/draws?playday_id=${selectedPlayday}`);
      setDraws(response.data);
      if (response.data.length > 0) {
        setSelectedDraw(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch draws");
    }
  };

  const handleTimeChange = (entryId, field, value) => {
    setTimes(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const timeInputs = Object.entries(times).map(([entryId, data]) => ({
        draw_entry_id: entryId,
        time: data.time ? parseFloat(data.time) : null,
        penalty: data.penalty ? parseFloat(data.penalty) : null
      }));

      await axios.put(`${API}/draws/${selectedDraw}/times`, timeInputs);
      toast.success("Times saved successfully");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save times");
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      await axios.put(`${API}/draws/${selectedDraw}/unlock`);
      toast.success("Draw unlocked - you can now edit times");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock draw");
    } finally {
      setUnlocking(false);
    }
  };

  const isPlaydayFinalized = () => {
    const playday = playdays.find(p => p.id === selectedPlayday);
    return playday?.is_finalized || false;
  };

  const isRopingEvent = (eventName) => {
    return eventName?.includes("Team Roping") || eventName?.includes("Ribbon Roping");
  };

  const isDrawLocked = () => {
    return currentDraw?.is_locked || false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="input-times-page">
      <div>
        <h1 className="font-heading font-bold text-3xl text-text-primary">Input Times</h1>
        <p className="text-text-secondary mt-1">Enter times and penalties for each contestant</p>
      </div>

      {/* Selectors */}
      <div className="flex gap-4 items-end flex-wrap">
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
          <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
            <SelectTrigger data-testid="playday-select">
              <SelectValue placeholder="Select a play day" />
            </SelectTrigger>
            <SelectContent>
              {playdays.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.date}) {p.is_finalized && "✓"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Event</label>
          <Select value={selectedDraw} onValueChange={setSelectedDraw}>
            <SelectTrigger data-testid="event-select">
              <SelectValue placeholder="Select an event" />
            </SelectTrigger>
            <SelectContent>
              {draws.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.event_name} {d.is_locked && "🔒"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Show Unlock button if locked, otherwise Save button */}
        {isDrawLocked() ? (
          <Button 
            onClick={handleUnlock} 
            disabled={unlocking || isPlaydayFinalized()}
            variant="outline"
            className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
            data-testid="unlock-times-btn"
          >
            <Unlock className="h-4 w-4 mr-2" />
            {unlocking ? "Unlocking..." : "Unlock to Edit"}
          </Button>
        ) : (
          <Button 
            onClick={handleSave} 
            disabled={saving || !currentDraw || isPlaydayFinalized()}
            className="bg-status-success text-white hover:bg-status-success/90"
            data-testid="save-times-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Times"}
          </Button>
        )}
      </div>

      {/* Locked Warning Banner */}
      {isDrawLocked() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-2">
          <Lock className="h-5 w-5 text-yellow-600" />
          <span className="text-yellow-800 font-medium">
            This draw is locked (payout/points applied). Click "Unlock to Edit" to modify times.
          </span>
        </div>
      )}

      {/* Playday Finalized Warning */}
      {isPlaydayFinalized() && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800 font-medium">
            This playday is finalized. Go to Draws page to unlock the playday first.
          </span>
        </div>
      )}

      {/* Time Input Table */}
      {currentDraw ? (
        <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
          <div className="p-4 border-b border-primary/20 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-lg text-text-primary flex items-center gap-2">
                {currentDraw.event_name}
                {isDrawLocked() && <Lock className="h-4 w-4 text-yellow-600" />}
              </h3>
              <p className="text-sm text-text-secondary">{currentDraw.entries.length} contestants</p>
            </div>
            {currentDraw.is_finalized && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-status-success/20 text-status-success rounded-full text-sm font-medium">
                Finalized
              </span>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="font-heading font-bold text-text-primary w-20">#</TableHead>
                <TableHead className="font-heading font-bold text-text-primary">Contestant 1</TableHead>
                {isRopingEvent(currentDraw.event_name) && (
                  <TableHead className="font-heading font-bold text-text-primary">Contestant 2</TableHead>
                )}
                <TableHead className="font-heading font-bold text-text-primary w-40">Time (seconds)</TableHead>
                <TableHead className="font-heading font-bold text-text-primary w-40">Penalty (seconds)</TableHead>
                <TableHead className="font-heading font-bold text-text-primary w-32 text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentDraw.entries.map((entry) => {
                const time = times[entry.id]?.time || "";
                const penalty = times[entry.id]?.penalty || "";
                const total = time ? (parseFloat(time) + (penalty ? parseFloat(penalty) : 0)).toFixed(3) : "-";
                
                return (
                  <TableRow key={entry.id} className="hover:bg-surface-muted" data-testid={`time-entry-${entry.id}`}>
                    <TableCell className="font-heading font-bold text-lg text-primary">
                      {entry.draw_order}
                    </TableCell>
                    <TableCell className="font-medium text-text-primary">
                      {entry.contestant_name}
                    </TableCell>
                    {isRopingEvent(currentDraw.event_name) && (
                      <TableCell className="text-text-secondary">
                        {entry.contestant2_name || "-"}
                      </TableCell>
                    )}
                    <TableCell>
                      <Input
                        type="number"
                        step="0.001"
                        placeholder="0.000"
                        value={time}
                        onChange={(e) => handleTimeChange(entry.id, "time", e.target.value)}
                        disabled={isDrawLocked() || isPlaydayFinalized()}
                        className={`font-mono time-input h-9 ${isDrawLocked() ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        data-testid={`time-input-${entry.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="0.0"
                        value={penalty}
                        onChange={(e) => handleTimeChange(entry.id, "penalty", e.target.value)}
                        disabled={isDrawLocked() || isPlaydayFinalized()}
                        className={`font-mono time-input h-9 ${isDrawLocked() ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        data-testid={`penalty-input-${entry.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-text-primary">
                      {total !== "-" ? `${total}s` : total}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-primary/20 p-12 text-center">
          <Clock className="h-12 w-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary">Select a play day and event to input times</p>
        </div>
      )}
    </div>
  );
};

export default InputTimesPage;
